import {
  call, put, select, takeLatest, takeEvery, all,
} from 'redux-saga/effects';
import findResult, {
  findSelectedInCategory,
  findWithReadStatus,
  successAndErrors,
  wildcardFilter,
} from '@/utils/resultsHelper';
import { indexOfUUID, spliceItemByUUID } from '@/utils/uuidHelper';
import { states } from '@/constants/lifecycle';
import { CARDS_PAGE_SIZE, presentationOptions } from '@/constants/states';
import { roleChanged, roleUpdated } from '@/users/actions';
import { showConfigurationSuccess, showConfigurationError } from '@/configurations/actions';
import { configurationCodes } from '@/configurations/constants';
import ConfigurationService from '@/configurations/service';
import { toggleOmniSearchFlags } from '@/search/actions';
import selectors from '@/selectors';
import callAdapter from '@/utils/sagaHelper';
import RolesService from '../service';
import {
  showRoles,
  showRolesError,
  showRole,
  showRoleError,
  newRole,
  newRoleError,
  updateRoleSuccess,
  updateRoleError,
  editRoleError,
  editRoleSuccess,
  applyDefaultRoleCode,
} from '../actions';
import actions, { roleLists } from '../constants';
import { duplicateRoleActions } from '../roleactions/sagas/api';
import { refreshRoleActions } from '../roleactions/actions';

export function* roles(action) {
  const items = yield select(selectors.roles);
  const {
    display, filter, pageSize, currentPage, userFlag, tokenFlag, assignable,
  } = items[action.category];

  const page = action.page || currentPage;
  const size = action.pageSize || pageSize;
  const includeUsers = action.userFlag || userFlag;
  const includeTokens = action.tokenFlag || tokenFlag;
  const { response, error } = yield call(callAdapter, RolesService.roles(
    action.category,
    wildcardFilter(filter),
    includeUsers,
    includeTokens,
    page,
    display === presentationOptions.CARDS ? CARDS_PAGE_SIZE : size,
    assignable,
  ));

  if (error) {
    yield put(showRolesError(action.category, error));
  } else {
    yield put(showRoles(
      action.category,
      action.append,
      size,
      response.items,
      response.paging.total,
      response.paging.pages,
      response.paging.page,
    ));
  }
}

export function* role({ category, code }) {
  const { response, error } = yield call(callAdapter, RolesService.role(code));
  if (error) {
    yield put(showRoleError(category, code, error));
  } else {
    yield put(showRole(category, code, response));
  }
}

export function* defaultRole() {
  const { response, error } = yield call(
    callAdapter,
    ConfigurationService.configuration(configurationCodes.DEFAULT_ROLE),
  );

  if (error) {
    yield put(showConfigurationError('all', configurationCodes.DEFAULT_ROLE, error));
    yield put(showRoleError('defaultRole', null, error));
  } else {
    yield put(showConfigurationSuccess('all', configurationCodes.DEFAULT_ROLE, response));
    yield put(applyDefaultRoleCode(response.value.value));
    yield call(role, { category: 'defaultRole', code: response.value.value });
  }
}

export function* create({ token }) {
  const item = yield select(selectors.activeRole);
  const code = token ? item.tokenRole : item.role;
  const basedOn = yield call(callAdapter, RolesService.role(code));

  if (basedOn.error) {
    yield put(newRoleError(basedOn.error));
    return;
  }

  const { permissions } = basedOn.response;
  const maxActive = parseInt(basedOn.response.max_active_tokens, 10);
  const maxDays = parseInt(basedOn.response.max_token_length_days, 10);
  const { response, error } = yield call(callAdapter, RolesService.create(item, maxActive, maxDays, permissions));

  if (error) {
    yield put(newRoleError(error));
    return;
  }

  const items = yield select(selectors.roles);
  const toRefresh = findWithReadStatus(items, roleLists, states.SUCCESS);

  yield put(newRole(response));
  yield all(toRefresh.map((category) => roles({ category })));

  yield duplicateRoleActions(code, response.code);
}

export function* update(action) {
  const items = yield select(selectors.roles);
  const item = findResult(items, action.category, action.code);
  const maxActive = parseInt(item.max_active_tokens, 10);
  const maxDays = parseInt(item.max_token_length_days, 10);
  const { response, error } = yield call(callAdapter, RolesService.update(item, maxActive, maxDays));

  if (error) {
    yield put(editRoleError(action.category, action.code, error));
  } else {
    yield put(roleUpdated(response.code, response));
    yield put(editRoleSuccess(action.category, response.code, response));
  }
}

function updateUserRole(code, user) {
  return RolesService.updateRole(code, user.uuid)
    .then((response) => ({ response, user }))
    .catch((error) => ({ error, user }));
}

export function* updateUsersRole() {
  const { currentUser } = yield select(selectors.auth);
  const items = yield select(selectors.roles);
  const { code } = findSelectedInCategory(items, 'reassign')[0];
  const users = items.reassign.users.filter((u) => u.role?.code !== code);

  const me = spliceItemByUUID(users, currentUser.id);
  if (me) {
    users.push(me);
  }

  const results = yield all(users.map((u) => updateUserRole(code, u)));
  const { success, errors } = successAndErrors(results);

  if (errors.length > 0) {
    yield put(updateRoleError(errors));
  }
  if (success.length > 0) {
    const reassignedUsers = success.map((r) => r.user);
    const [{ response }] = success;

    if (me && indexOfUUID(reassignedUsers, currentUser.id) > -1) {
      yield put(roleChanged(response));
      yield put(refreshRoleActions(response.code));

      const permissions = results.flatMap((item) => item.response.permissions);
      const searchRun = permissions.some(({ category, action }) => category === 'search' && action === 'run');
      const datasetsRead = permissions.some(({ category, action }) => category === 'datasets' && action === 'read');
      if (!searchRun || !datasetsRead) {
        yield put(toggleOmniSearchFlags('home', null, true, true, false));
      } else {
        yield put(toggleOmniSearchFlags('home', null, false, false, true));
      }
    }
    yield put(updateRoleSuccess(response, reassignedUsers));
  }
}

export function* refreshRolesPresentation(action) {
  yield call(roles, { ...action, page: 1, append: false });
}

export default function* rolesApi() {
  yield takeEvery([actions.FETCH_ROLES, actions.REFRESH_ROLES, actions.UPDATE_ROLES_FLAGS], roles);
  yield takeEvery(actions.FETCH_ROLE, role);
  yield takeLatest(actions.FETCH_DEFAULT_ROLE, defaultRole);
  yield takeLatest(actions.CREATE_ROLE, create);
  yield takeEvery(actions.UPDATE_ROLE, update);
  yield takeEvery(actions.UPDATE_USERS_ROLE, updateUsersRole);
  yield takeEvery(actions.TOGGLE_ROLES_PRESENTATION, refreshRolesPresentation);
}
